import React from "react";
import {useSnackbar} from "notistack";
import {ANONYMOUS_KEY, API_URL} from "../constants";
import {atom, useRecoilState, useSetRecoilState} from "recoil";
import {localStorageEffect} from "./localStorage";
import jwtDecode from "jwt-decode";
import {useHistory} from "react-router-dom";
import {Box} from "@mui/material";
import CircularProgressWithLabel from "../components/CircularProgress/CircularProgressWithLabel";
import axios, {AxiosResponse} from 'axios';

export const tokenState = atom({
    key: 'tokenState', // unique ID (with respect to other atoms/selectors)
    default: null, // default value
    effects_UNSTABLE: [
        localStorageEffect('token'),
    ]
});

export const rolesState = atom({
    key: 'rolesState',
    default: [],
    effects_UNSTABLE: [
        localStorageEffect('roles'),
    ]
})

export const includePublicCollectionsState = atom({
    key: 'include_public_collections',
    default: false,
    effects_UNSTABLE: [
        localStorageEffect('include_public_collections'),
    ]
})

export const subState = atom({
    key: 'subState', // unique ID (with respect to other atoms/selectors)
    default: 'readonly', // default value
    effects_UNSTABLE: [
        localStorageEffect('sub'),
    ]
})

function WaitingServer() {
    const setToken = useSetRecoilState(tokenState);
    const [ping, setPing] = React.useState(true);

    const reconnect = () => {
        makeRequest(
            `${API_URL}/auth/reader`,
            'POST',
            ANONYMOUS_KEY,
            {
                'Content-Type': 'text/plain'
            }).then((response) => {
            if (response.status === 200) {
                setToken(response.data.token);
            }
        });
        setPing(false);
    }

    React.useEffect(() => {
        if (ping)
            reconnect();
        const timeoutId = window.setTimeout(() => {
            setPing(true);
        }, 10000);
        return () => {
            window.clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ping]);

    return (
        <Box width="100%" height="100vh" display="flex" alignItems="center" justifyContent="center">
            <CircularProgressWithLabel label="Waiting for server" size={150} color={"secondary"}/>
        </Box>
    );
}

function RequestService({children}) {
    const {enqueueSnackbar} = useSnackbar();
    const [token, setToken] = useRecoilState(tokenState);
    const setRoles = useSetRecoilState(rolesState);
    const setSub = useSetRecoilState(subState);
    const history = useHistory();

    React.useEffect(() => {
        if (!Boolean(token)) {
            makeRequest(
                `${API_URL}/auth/reader`,
                'POST',
                ANONYMOUS_KEY,
                {
                    'Content-Type': 'text/plain'
                }).then((response) => {
                if (response.status === 200) {
                    setToken(response.data.token);
                    setRoles(response.data.roles);
                }
            });
        }
    });

    React.useEffect(() => {
        if (token) {
            let jwtData = jwtDecode(token);
            setSub(jwtData.sub);
        } else {
            setSub(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    /**
     * Intercepts HTTP requests to insert the token if it exists
     */
    axios.interceptors.request.use((config) => {
        let storage = localStorage.getItem("token");
        if (storage) {
            try {
                let t = JSON.parse(storage);
                if (t)
                    config.headers['Authorization'] = `Bearer ${t}`;
            } catch (e) {
                enqueueSnackbar("Cannot parse token", {variant: "error"});
                setToken(null);
                setSub(null);
            }
        }

        const parsed = new URL(config.url);

        if (parsed.pathname && parsed.pathname.endsWith("/")) {
            parsed.pathname = parsed.pathname.substring(0, parsed.pathname.length - 1);
        }

        config.url = parsed.toString();

        return config;
    });

    /**
     * Intercepteur de réponses HTTP avec traitement du status 401
     */
    axios.interceptors.response.use(response => {
        return response;
    }, error => {
        // Fonction de traitement des erreurs
        async function handleError() {
            if (error.response) {
                // Erreur 401
                if (error.response.status === 401) {
                    setToken(null);
                } else if (error.response.status === 403) {
                    if (token) {
                        let jwtData = jwtDecode(token);
                        setSub(jwtData.sub);
                    } else {
                        setSub(null);
                    }
                    if (error.response.data && error.response.data.message)
                        enqueueSnackbar(error.response.data.message, {variant: "error"});
                } else {
                    if (error.response.headers['content-type'] === 'application/json') {
                        let data = error.response.data;
                        if (data.message)
                            enqueueSnackbar(data.message, {variant: "error"});
                        if (data.messages)
                            Object.keys(data.messages).forEach((key) => {
                                    enqueueSnackbar(`${key.toUpperCase()} : ${data.messages[key]}`, {variant: "error"});
                                }
                            );
                    }
                }
                return {status: error.response.status};
            } else if (error.request) {
                // Pas de réponse du serveur
                history.push("/error/noresponse");
                return {status: error.request.status};
            } else {
                return error;
            }
        }

        return handleError();
    });

    return Boolean(token)
        ? children
        : <WaitingServer/>;
}

/**
 * Fonction permettant d'exécuter une requête HTTP avec Axios
 * @param url L'URL de l'API
 * @param method La méthode d'appel (GET, POST, PUT, DELETE)
 * @param data Le corps du message à envoyer
 * @param headers {Object}
 * @param responseType le type de réponse attendue
 * @returns {Promise<AxiosResponse<any>>} Une response HTTP
 */
export function makeRequest(
    url,
    method = 'GET',
    data = null,
    headers = {Accept: "application/json"}) {
    const config = {
        method: method,
        url: encodeURI(url),
        data: data,
        headers: headers,
    }
    return axios(config)
        .then((response) => {
            return response;
        }).catch(error => {
            return (error);
        });
}

export default RequestService;